import {
  useRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useReducer,
} from "react";
import { Badge, Pagination, Table } from "rsuite";
import { ApiCompany } from "../../core/apis";
import {
  ApiSchemaCompany,
  ApiSchemaCompanyPaginated,
} from "../../models/api/company";
import { useHistory } from "react-router-dom";
import { AppRouteApi, AppRouteUi } from "../../config/appRoute";
import { useAppSelector } from "../../core/redux";
import { useAppDispatch } from "../../core/redux";
import { PaginatedData } from "../../models/api/paginatedData";
import { ActionsCompanyTable } from "../../core/redux/slices/sliceCompanyTable";

import {
  ActionCell,
  NumberCell,
  SeeInsuranceTypesCell,
  TextCell,
} from "../../components/tableItem.component";
import { useAlertDialog } from "../../components/alertDialog";
import { showToast } from "../../components/toast.component";
import { GPSCheckInToggleForCompany } from "../../components/gpsCheckInToggleForCompany.component";
import { AutoClockToggleForCompany } from "../../components/autoClockToggleForCompany.component";
import { sortTableData } from "../../utils/tableFilter";
import { BooleanToggleFilterIcon } from "../../components/booleanToggleFilterIcon.component";
import {
  useTablePaginationSearchFilter,
  IPaginationSearchFilter,
} from "../../hooks";
import { TableHeader } from "../../components";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaCompany & {
  sl: number;
  checked?: boolean;
  name?: string;
};

const BadgeCell = ({
  rowData,
  dataKey,
  ...props
}: {
  rowData?: TableData;
  dataKey?: keyof TableData;
}) => {
  const getColor = (status: string) =>
    status === "Active"
      ? "#00BDB4"
      : status === "Inactive"
      ? "#FF0000"
      : "#808080";

  return (
    <Cell {...props}>
      {rowData && dataKey && (
        <Badge
          content={rowData[dataKey]}
          style={{ background: getColor(rowData[dataKey] as string) }}
          className="p-1 px-2"
        />
      )}
    </Cell>
  );
};

export const CompanyList = () => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [selected, setSelected] = useState<TableData[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const triggerRef = useRef<any>();
  const routerHistory = useHistory();
  const {
    pageNumber,
    pageSize,
    isLoading,
    searchKey,
    nextPageToken,
    shouldRefetch,
  } = useAppSelector((state) => state.companyTable);
  const dispatch = useAppDispatch();
  const { alertDialogCtrl, alertDialogComponent } = useAlertDialog();

  const [sortBy, setSortBy] = useState<keyof TableData>("sl");
  const [sortAsc, setSortAsc] = useState(true);
  const [filterGPSCheckIn, toggleFilterGPSCheckIn] = useReducer(
    (curr: number) => (curr + 1) % 3,
    2
  );
  const [filterAutoClockCheckIn, toggleFilterAutoClockCheckIn] = useReducer(
    (curr: number) => (curr + 1) % 3,
    2
  );

  const customParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      PageNumber: pageNumber,
      PageSize: pageSize,
      SearchKey: searchKey,
      NextPageToken: nextPageToken,
    };
  }, [pageNumber, pageSize, searchKey, nextPageToken]);
  const { apiData, refetch } = useTablePaginationSearchFilter<
    ApiSchemaCompanyPaginated,
    typeof ActionsCompanyTable
  >(ActionsCompanyTable, {
    customParams,
    apiRoute: AppRouteApi.Company.Root(),
  });

  useEffect(
    () =>
      setTableData(
        apiData?.list
          ?.filter((item) => item.name.toLowerCase() !== "evv")
          .map((company, index) => ({
            sl: (pageNumber - 1) * pageSize + index + 1,
            ...company,
          })) ?? []
      ),
    [apiData, pageNumber, pageSize]
  );

  const CellRowSelection = useMemo((): ReactNode => {
    return (rowData: TableData) => (
      <input
        type="checkbox"
        checked={!!rowData.checked}
        onChange={(ev) =>
          rowData.sKey &&
          setTableData((tableData) =>
            tableData?.map((td) =>
              td.sKey === rowData.sKey
                ? {
                    ...td,
                    checked: ev.target.checked,
                  }
                : td
            )
          )
        }
      />
    );
  }, []);

  const selectAllRow = useCallback((checked: boolean) => {
    setTableData((tableData) =>
      tableData.map((td) => ({
        ...td,
        checked: checked,
      }))
    );
  }, []);

  const isAllChecked = useMemo(
    () => tableData.length > 0 && tableData.every((td) => !!td.checked),
    [tableData]
  );

  useEffect(() => {
    setSelected(tableData.filter((td) => !!td.checked));
  }, [tableData]);

  const updateDeleted =
    (sKeys: string[]) => async (res?: PaginatedData<ApiSchemaCompany>) => {
      let updated: PaginatedData<ApiSchemaCompany> = {
        totalCount: res?.totalCount || 0,
        list: res?.list.filter((i) => !sKeys.includes(i.sKey)) || [],
      };
      return updated;
    };

  const onDelete = useCallback(
    () =>
      alertDialogCtrl.open({
        message: "Are you sure you want to delete the selected companies?",
        body: (
          <p>
            Users, tasks, appointments and financial information of those
            companies will also be deleted. Please proceed with caution.
          </p>
        ),
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          setIsDeleting(true);
          dispatch(ActionsCompanyTable.setIsLoading(true));
          const selectedIds = selected.filter((s) => s.sKey).map((s) => s.sKey);
          await deleteItem(selectedIds);
          const sKeys = selected.map((item) => item.sKey);
          ApiCompany.GetPaginated.Mutate(updateDeleted(sKeys));
          setIsDeleting(false);
          dispatch(ActionsCompanyTable.setIsLoading(false));
          dispatch(ActionsCompanyTable.setRefetch(true));
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  );

  const handleSelectMenu = (eventKey: string, event: Event, sKey?: string) => {
    if (eventKey === "delete" && sKey) {
      alertDialogCtrl.open({
        message: "Are you sure you want to delete this company?",
        body: (
          <p className="text-red-300">
            Users, tasks, appointments and financial information of the company
            will also be deleted. Please proceed with caution.
          </p>
        ),
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          dispatch(ActionsCompanyTable.setIsLoading(true));
          const deleted = await deleteItem([sKey]);
          if (deleted) ApiCompany.GetPaginated.Mutate(updateDeleted([sKey]));
          dispatch(ActionsCompanyTable.setIsLoading(false));
        },
      });
    } else if (eventKey === "update" && sKey)
      routerHistory.push(AppRouteUi.Company.UpdateById(sKey));
  };

  const deleteItem = async (selectedSKeys: string[]) => {
    const motherSKey =
      tableData.find((td) => td.name.toLowerCase() === "webevv")?.sKey ?? "";
    if (selectedSKeys.includes(motherSKey)) {
      showToast("Mother company cannot be deleted!", "error");
      return false;
    }
    return await ApiCompany.Delete(selectedSKeys);
  };

  const filteredTableData = useMemo(() => {
    let filteredData =
      filterAutoClockCheckIn === 2
        ? tableData
        : tableData.filter(
            (td) => +td.isAutoClockInEnable === filterAutoClockCheckIn
          );

    filteredData =
      filterGPSCheckIn === 2
        ? filteredData
        : filteredData.filter(
            (td) => +td.isGpsCheckInEnable === filterGPSCheckIn
          );

    return sortTableData(filteredData, sortBy, sortAsc);
  }, [tableData, sortBy, sortAsc, filterAutoClockCheckIn, filterGPSCheckIn]);

  useEffect(() => {
    shouldRefetch && refetch();
  }, [shouldRefetch, refetch]);

  return (
    <div>
      {alertDialogComponent}
      <TableHeader
        buttonText="Add New Company"
        redirectPath={AppRouteUi.Company.Add()}
        onDelete={onDelete}
        pageSize={pageSize}
        TableAction={ActionsCompanyTable}
        isDeleting={isDeleting}
        isSelected={!!selected.length}
      />
      <Table
        autoHeight
        data={filteredTableData}
        loading={isLoading}
        onScroll={() => triggerRef.current?.close()}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Data Available</h3>
          </div>
        )}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortBy, sortAsc) => {
          setSortBy(sortBy as keyof TableData);
          setSortAsc(sortAsc === "asc");
        }}
      >
        <Column align="center" width={35}>
          <HeaderCell className="fw-bold text-black">
            <input
              type="checkbox"
              checked={isAllChecked}
              onChange={(ev) => selectAllRow(ev.target.checked)}
            />
          </HeaderCell>
          <Cell>{CellRowSelection}</Cell>
        </Column>
        <Column align="center" width={70} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="S/L">S/L</span>
          </HeaderCell>
          <TextCell component="Company" dataKey="sl" />
        </Column>

        <Column width={200} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Company Name">Company Name</span>
          </HeaderCell>
          <TextCell component="Company" dataKey="displayName" />
        </Column>

        <Column width={200} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="NPI/Provider Number">NPI/Provider Number</span>
          </HeaderCell>
          <TextCell component="Company" dataKey="providerNumber" />
        </Column>

        <Column width={100} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Users">Users</span>
          </HeaderCell>
          <NumberCell component="Company" dataKey="userCount" />
        </Column>

        <Column width={180}>
          <HeaderCell className="fw-bold text-black">
            <span title="Insurance Types">Insurance Types</span>
          </HeaderCell>
          <Cell>
            {(rowData?: TableData) => (
              <SeeInsuranceTypesCell
                insuranceTypeList={rowData?.insuranceTypes}
              />
            )}
          </Cell>
        </Column>

        <Column width={100}>
          <HeaderCell className="fw-bold text-black">
            <span title="Status">Status</span>
          </HeaderCell>
          <BadgeCell dataKey="status" />
        </Column>

        <Column align="center" width={110}>
          <HeaderCell className="fw-bold text-black">
            <span
              title="Auto Clock"
              className="click"
              onClick={() => {
                setSortBy("isAutoClockInEnable");
                toggleFilterAutoClockCheckIn();
              }}
            >
              Auto Clock
              <BooleanToggleFilterIcon
                filterValue={
                  filterAutoClockCheckIn === 0
                    ? "true"
                    : filterAutoClockCheckIn === 1
                    ? "false"
                    : "default"
                }
                className="ms-2 toggle-filter-icon"
              />
            </span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaCompany) => (
              <AutoClockToggleForCompany company={rowData} />
            )}
          </Cell>
        </Column>

        <Column align="center" width={150}>
          <HeaderCell className="fw-bold text-black">
            <span
              title="GPS Checked In"
              className="click"
              onClick={() => {
                setSortBy("isGpsCheckInEnable");
                toggleFilterGPSCheckIn();
              }}
            >
              GPS Checked In
              <BooleanToggleFilterIcon
                filterValue={
                  filterGPSCheckIn === 0
                    ? "true"
                    : filterGPSCheckIn === 1
                    ? "false"
                    : "default"
                }
                className="ms-2 toggle-filter-icon"
              />
            </span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaCompany) => (
              <GPSCheckInToggleForCompany company={rowData} />
            )}
          </Cell>
        </Column>

        <Column align="center" width={70} fixed="right">
          <HeaderCell className="fw-bold text-black">
            <span title="Action">Action</span>
          </HeaderCell>
          <ActionCell
            component="Company"
            handleSelectMenu={handleSelectMenu}
            triggerRef={triggerRef}
          />
        </Column>
      </Table>

      {apiData?.list && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={apiData?.list.length}
            pages={Math.ceil(apiData?.totalCount / pageSize)}
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page: any) =>
              dispatch(ActionsCompanyTable.goToPage(page))
            }
          />
        </div>
      )}
    </div>
  );
};
